import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { gbp, statusLabel } from "@/lib/format";

const NEXT = {
  submitted: ["clarification_requested", "conditional_offer", "closed"], clarification_requested: ["information_provided", "conditional_offer", "closed"],
  conditional_offer: ["award_setup_complete", "closed"], award_setup_complete: ["active_project", "closed"], active_project: ["closed"],
  filed: ["search_requested", "published", "under_examination", "ended"], search_requested: ["published", "under_examination", "ended"],
  published: ["under_examination", "granted", "ended"], under_examination: ["granted", "ended"], granted: ["ended"],
};
const RESP_LABELS = { information_provided: "Information provided to the funder", closed: "Closed (unsuccessful, withdrawn or completed)", ended: "Ended (refused, withdrawn or lapsed)" };

const ResponseForm = ({ app, reload }) => {
  const opts = NEXT[app.status] || [];
  const [f, setF] = useState({ type: opts[0], note: "", due_date: "", awarded: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/applications/${app.id}/response`, { ...f, due_date: f.due_date || null, awarded: f.awarded ? Number(f.awarded) : null });
      toast.success("Official response recorded");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="official-response-form">
      <select aria-label="Response type" data-testid="response-type-select" className="input" value={f.type} onChange={set("type")}>
        {opts.map((o) => <option key={o} value={o}>{RESP_LABELS[o] || statusLabel(o, app.kind)}</option>)}
      </select>
      <input aria-label="Date" type="date" data-testid="response-date-input" className="input" value={f.due_date} onChange={set("due_date")} title="Clarification due date, or grant/registration date" />
      {f.type === "conditional_offer" && <input aria-label="Amount offered" type="number" data-testid="response-awarded-input" className="input" placeholder="Amount offered (£)" value={f.awarded} onChange={set("awarded")} />}
      <input aria-label="Note" className="input" placeholder={["closed", "ended"].includes(f.type) ? "Reason (required)" : "Note"} value={f.note} onChange={set("note")} />
      <button data-testid="response-submit-button" className="btn-teal btn-sm sm:w-fit">Record response</button>
    </form>
  );
};

export default function TrackingPanel({ app, reload }) {
  const [amounts, setAmounts] = useState(app.amounts);
  const [ip, setIp] = useState(app.ip);
  const [rep, setRep] = useState({ label: "", date: "" });
  const save = async (body) => {
    try {
      await api.put(`/applications/${app.id}`, body);
      toast.success("Tracking updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const canRespond = !!NEXT[app.status];
  return (
    <section className="card p-6" data-testid="tracking-panel">
      <h2 className="font-semibold text-[#0B192C]">Tracking</h2>
      {app.kind === "grant" ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <div><p className="caption">Requested</p><p className="font-mono" data-testid="amount-requested">{gbp(app.amounts.requested)}</p></div>
          <div><label className="caption" htmlFor="t-aw">Awarded</label><input id="t-aw" type="number" className="input" data-testid="amount-awarded-input" value={amounts.awarded ?? ""} onChange={(e) => setAmounts({ ...amounts, awarded: e.target.value })} /></div>
          <div><label className="caption" htmlFor="t-pd">Paid to date</label><input id="t-pd" type="number" className="input" data-testid="amount-paid-input" value={amounts.paid ?? ""} onChange={(e) => setAmounts({ ...amounts, paid: e.target.value })} /></div>
          <button onClick={() => save({ amounts: { ...amounts, awarded: amounts.awarded === "" ? null : Number(amounts.awarded), paid: amounts.paid === "" ? null : Number(amounts.paid) } })} data-testid="save-amounts-button" className="btn-ghost btn-sm self-end">Save amounts</button>
        </div>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <div><p className="caption">Application no.</p><p className="font-mono text-sm">{app.ip.application_number || "—"}</p></div>
          <div><p className="caption">Filed</p><p className="font-mono text-sm" data-testid="ip-filing-date">{app.ip.filing_date || "Not filed"}</p></div>
          <div><label className="caption" htmlFor="t-reg">Registered / granted</label><input id="t-reg" type="date" className="input" value={ip.registration_date || ""} onChange={(e) => setIp({ ...ip, registration_date: e.target.value })} /></div>
          <button onClick={() => save({ ip })} className="btn-ghost btn-sm self-end">Save</button>
          <p className="text-xs text-slate-500 sm:col-span-4">Filing is not registration. A filed application may still be refused or opposed.</p>
        </div>
      )}
      {app.kind === "grant" && (
        <div className="mt-5">
          <p className="text-sm font-medium">Grant reporting obligations</p>
          <ul className="mt-1 text-sm">{(app.reporting_dates || []).map((r, i) => <li key={i}>{r.date} — {r.label}</li>)}</ul>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input aria-label="Report name" className="input" placeholder="e.g. Quarterly claim" value={rep.label} onChange={(e) => setRep({ ...rep, label: e.target.value })} />
            <input aria-label="Report date" type="date" className="input sm:w-48" value={rep.date} onChange={(e) => setRep({ ...rep, date: e.target.value })} />
            <button disabled={!rep.date} onClick={() => { save({ reporting_dates: [...(app.reporting_dates || []), rep] }); setRep({ label: "", date: "" }); }} data-testid="add-reporting-date" className="btn-ghost btn-sm">Add</button>
          </div>
        </div>
      )}
      <div className="mt-6 border-t border-slate-100 pt-4">
        <p className="text-sm font-medium">Official responses</p>
        {canRespond ? <ResponseForm key={app.status} app={app} reload={reload} /> : <p className="text-xs text-slate-500">{["closed", "ended"].includes(app.status) ? `This application is ${app.status}${app.closed_reason ? `: ${app.closed_reason}` : ""}.` : "Available after a verified submission is recorded."}</p>}
      </div>
      {!["closed", "ended"].includes(app.status) && (
        <button onClick={() => { const note = window.prompt("Reason for closing (e.g. withdrawn)"); if (note) api.put(`/applications/${app.id}/status`, { status: app.kind === "grant" ? "closed" : "ended", note }).then(reload).catch((e) => toast.error(errMsg(e))); }} data-testid="withdraw-button" className="mt-6 text-xs text-red-600 hover:underline">Withdraw / close application</button>
      )}
    </section>
  );
}
